export const en = {
    ReviewCyclesList: {
        totalCount_one: "{{count}} review cycle",
        totalCount_other: "{{count}} review cycles",
        noData: "No review cycle has been created yet.",
        noResults: "No matching review cycles",
        noResultsHint: "Try a different search term, or clear some filters.",
        error: "We couldn't load the review cycles",
        liveRegion: {
            loading: "Loading review cycles",
            loadingMoreAnnouncement: "Loading more review cycles",
            loadedMore_one: "{{count}} more review cycle loaded",
            loadedMore_other: "{{count}} more review cycles loaded",
            results_one: "{{count}} review cycle",
            results_other: "{{count}} review cycles"
        }
    },
    ReviewCyclesTable: {
        ariaLabel: "Review cycles",
        columns: {
            name: "Cycle",
            state: "State",
            reviewees: "Reviewees",
            reviewTypes: "Types",
            progress: "Progress",
            evaluationPeriod: "Eval period",
            creator: "Created by",
            actions: "Actions"
        },
        unknownCreator: "Unknown",
        untitled: "Untitled review cycle",
        noEvaluationPeriod: "—",
        progressLabel: "{{submitted}} of {{total}} reviews submitted",
        noProgress: "Not started"
    },
    ReviewCyclesToolbar: {
        searchPlaceholder: "Search review cycles",
        searchAriaLabel: "Search review cycles",
        resetFilters: "Reset filters",
        addFilter: "Add filter",
        quickFilters: "Quick filters",
        filtersHeading: "Filters",
        applied: "Applied",
        removeFilter: "Remove filter",
        chipAriaLabel: "{{kind}}: {{value}}. Change or remove this filter",
        valueCount_one: "{{count}} selected",
        valueCount_other: "{{count}} selected",
        filters: {
            back: "Back to filters",
            noOptions: "Nothing to filter on yet",
            noMatches: "No matches",
            participant: "Participant",
            participantSearchPlaceholder: "Search people",
            participantLoading: "Loading people",
            participantNoResults: "No matching people",
            participantResults_one: "{{count}} person",
            participantResults_other: "{{count}} people",
            evaluationPeriod: "Eval period",
            evaluationPeriodHint: "Shows cycles evaluating any part of the selected year.",
            reviewType: "Review type",
            creator: "Created by",
            creatorSearchPlaceholder: "Search creators",
            state: "Cycle state"
        },
        presets: {
            "all-active": "All active cycles",
            "involving-me": "Active cycles involving me",
            "created-by-me": "Cycles I created"
        }
    },
    CreateReviewCycleButton: {
        label: "New review cycle"
    },
    CycleStateBadge: {
        draft: "Draft",
        scheduled: "Scheduled",
        active: "Active",
        locked: "Locked"
    },
    CycleReviewTypeBadge: {
        self: "Self",
        manager: "Manager",
        peer: "Peer",
        upward: "Upward"
    },
    CycleNudgeButton: {
        label: "Send a nudge",
        unavailable: {
            draft: "This cycle hasn't launched yet, so there is nobody to nudge.",
            locked: "This cycle is locked, so there is nothing left to submit."
        }
    },
    CycleNudgeDialog: {
        title: "Send a nudge",
        description: "When you send a nudge, each participant receives a reminder with the tasks they still need to finish.",
        nudgeToHeading: "Nudge people to:",
        loading: "Checking what is outstanding",
        loadFailed: "We couldn't check what is outstanding. Close this and try again.",
        nothingToRemind: "Nothing is outstanding in this cycle right now.",
        nudgeTo: {
            "self-review": "Complete their self-review",
            "manager-review": "Complete their manager reviews",
            "peer-review": "Complete their peer reviews",
            "upward-review": "Complete their upward reviews",
            "not-shared": "Share results with the reviewee",
            "not-acknowledged": "Acknowledge their review"
        },
        reminderType: {
            "self-review": "self-reviews",
            "manager-review": "manager reviews",
            "peer-review": "peer reviews",
            "upward-review": "upward reviews",
            "not-shared": "unshared results",
            "not-acknowledged": "unacknowledged reviews"
        },
        due: {
            daysLeft_one: "{{count}} day left",
            daysLeft_other: "{{count}} days left",
            today: "Due today",
            overdue_one: "Due {{count}} day ago",
            overdue_other: "Due {{count}} days ago"
        },
        sentRecentlyBadge: "Recently sent",
        recipients: {
            count_one: "{{count}} person",
            count_other: "{{count}} people",
            none: "Nobody outstanding",
            loading: "Loading recipients",
            error: "We couldn't load the recipients.",
            andMore_one: "and {{count}} more",
            andMore_other: "and {{count}} more"
        },
        recentlySent_one: "A nudge already went out in the last {{hours}} hours for: {{types}}. Sending again will nudge the same people twice.",
        recentlySent_other: "Nudges already went out in the last {{hours}} hours for: {{types}}. Sending again will nudge the same people twice.",
        message: {
            label: "Add a message to your nudge",
            sentAs: "This message will be sent as",
            placeholder: "You have review tasks that still need your attention. Please complete them as soon as possible."
        },
        sendToCount_one: "Nudge {{count}} person",
        sendToCount_other: "Nudge {{count}} people",
        sentToast_one: "Nudge sent to {{count}} person.",
        sentToast_other: "Nudges sent to {{count}} people.",
        sendFailedToast: "We couldn't send the nudges. Try again.",
        cancel: "Cancel",
        send: "Send nudges",
        sending: "Sending…"
    },
    CycleActionsMenu: {
        ariaLabel: "Actions for {{name}}",
        cancel: "Cancel",
        unavailable: {
            locked: "This cycle is locked.",
            notLaunched: "This cycle hasn't launched yet."
        },
        edit: { label: "Edit" },
        duplicate: {
            label: "Duplicate",
            success: "Cycle duplicated",
            error: "Your cycle couldn't be duplicated. Please try again."
        },
        lock: {
            label: "Close and lock",
            title: "Lock this cycle?",
            description: "Locking this cycle will block new submissions, resubmissions, and edits to the cycle.",
            descriptionUnsubmitted_one: "{{count}} review still needs to be submitted. Locking this cycle will block new submissions, resubmissions, and edits to the cycle.",
            descriptionUnsubmitted_other: "{{count}} reviews still need to be submitted. Locking this cycle will block new submissions, resubmissions, and edits to the cycle.",
            confirm: "Lock cycle",
            success: "Cycle locked",
            error: "Your cycle couldn't be locked. Please try again."
        },
        unlock: {
            label: "Unlock",
            title: "Unlock this cycle?",
            description: "Unlocking this cycle will allow submissions, resubmissions, and edits to the cycle.",
            confirm: "Unlock cycle",
            success: "Cycle unlocked",
            error: "Your cycle couldn't be unlocked. Please try again."
        },
        delete: {
            label: "Delete",
            title: "Delete {{name}}?",
            description: "This action is permanent and cannot be undone. All reviews will be permanently deleted, and features like export and calibration will no longer be available for this cycle.",
            confirmation: "I understand",
            confirm: "Delete cycle",
            success: "Cycle deleted",
            error: "Your cycle couldn't be deleted. Please try again."
        }
    },
    CycleDetailHeader: {
        status: "Status",
        evaluationPeriod: "Evaluation period",
        launchDate: "Launched",
        lockedDate: "Locked",
        automatedStartDate: "Starts",
        creator: "Created by",
        unknownCreator: "Unknown"
    },
    CycleExportMenu: {
        label: "Export",
        preparing: "Preparing…",
        failed: "That export couldn't be prepared. Try again.",
        group: {
            allReviews: "All reviews",
            report: "{{tab}} report"
        },
        item: {
            "all-reviews-pdf": "One file per reviewee",
            "all-reviews-csv": "All in one file",
            "report-pdf": "Charts and tables",
            "report-csv": "Raw data"
        },
        tab: {
            progression: "Progression",
            scores: "Scores",
            questions: "Question results",
            calibration: "Calibration",
            themes: "Themes"
        },
        liveRegion: {
            preparing: "Preparing your export."
        }
    },
    CycleProgressTracker: {
        typeLabel: "{{type}} reviews",
        submittedOf: "Submitted ({{submitted}}/{{total}})",
        distributionLabel: "{{type}}: {{breakdown}}",
        distributionBand: "{{percent}}% {{label}} ({{value}} of {{total}})",
        dueOn: "Due {{date}}"
    },
    CycleRevieweesTable: {
        ariaLabel: "Reviewees",
        columns: {
            reviewee: "Reviewee",
            state: "State",
            delivery: "Delivery",
            sortState: {
                ascending: "(sorted ascending)",
                descending: "(sorted descending)"
            }
        },
        cell: {
            noReview: "{{type}}: no reviewer assigned",
            status: "{{type}}: {{status}}",
            submittedOf: "{{type}}: {{submitted}} of {{total}} submitted",
            noPacket: "Delivery: nothing to deliver yet"
        },
        expandRow: "Show reviews for {{name}}",
        collapseRow: "Hide reviews for {{name}}",
        drawerRow: "Reviews for {{name}}",
        selectAll: "Select all reviewees"
    },
    CycleRosterSelectionBar: {
        selectedCount_one: "{{count}} selected",
        selectedCount_other: "{{count}} selected",
        allSelected: "All reviewees selected",
        allSelectedWithCount_one: "All {{count}} reviewee selected",
        allSelectedWithCount_other: "All {{count}} reviewees selected",
        share: "Share",
        unshare: "Unshare",
        remove: "Remove",
        unavailable: {
            locked: "This cycle is locked.",
            unknownCycle: "Still loading this cycle.",
            nothingShareable: "Only submitted reviews can be shared, and none of the selected reviews are submitted.",
            acknowledgmentRequired: "This cycle requires acknowledgment, so shared reviews can't be unshared.",
            nothingUnshareable: "Only shared reviews can be unshared, and none of the selected reviews are shared."
        }
    },
    CycleAddRevieweesDialog: {
        title: "Add reviewees",
        description: "Select the people or segments you want to add to this review cycle. The cycle's own rules decide when each of them enters.",
        searchLabel: "Search people and segments",
        searchPlaceholder: "Search by name",
        searchLoading: "Searching…",
        searchNoResults: "No matching people.",
        searchFailed: "We couldn't load the list. Check your connection and search again.",
        staleError: "Couldn't refresh — showing the last results.",
        searchResults_one: "{{count}} result",
        searchResults_other: "{{count}} results",
        liveRegion: {
            loadedMorePeople_one: "Loaded {{count}} more person",
            loadedMorePeople_other: "Loaded {{count}} more people",
            loadedMoreSegments_one: "Loaded {{count}} more segment",
            loadedMoreSegments_other: "Loaded {{count}} more segments"
        },
        peopleHeading: "People",
        segmentsHeading: "Segments",
        loadMorePeople: "Show more people",
        loadMoreSegments: "Show more segments",
        segmentOption_one: "{{name}} · {{count}} member",
        segmentOption_other: "{{name}} · {{count}} members",
        segmentTag_one: "{{name}} ({{count}})",
        segmentTag_other: "{{name}} ({{count}})",
        pickedMembers_one: "{{count}} person selected",
        pickedMembers_other: "{{count}} people selected",
        pickedSegments_one: "{{count}} segment selected",
        pickedSegments_other: "{{count}} segments selected",
        segmentCaveat: "A segment's member count is its whole population. How many of them join this cycle depends on the cycle's rules — the summary after you add will say.",
        cancel: "Cancel",
        confirm: "Add reviewees",
        adding: "Adding…",
        failedToast: "We couldn't add these reviewees. Try again.",
        forbiddenToast: "This cycle is locked, so its reviewees can't be changed.",
        outcome: {
            title: "Reviewees added",
            noChangesTitle: "No one was added",
            description: "The people and segments you selected have been applied to this review cycle.",
            nothingToReport: "Nothing changed for the people you selected.",
            groups: {
                added_one: "{{count}} person joined the cycle right away.",
                added_other: "{{count}} people joined the cycle right away.",
                addedUpcoming_one: "{{count}} person will enter later.",
                addedUpcoming_other: "{{count}} people will enter later.",
                alreadyAdded_one: "{{count}} person was already in this cycle.",
                alreadyAdded_other: "{{count}} people were already in this cycle.",
                missingHireDate_one: "{{count}} person has no hire date and was skipped.",
                missingHireDate_other: "{{count}} people have no hire date and were skipped.",
                unplaceable_one: "{{count}} person couldn't be added to this cycle.",
                unplaceable_other: "{{count}} people couldn't be added to this cycle."
            },
            nextEntry: "The first of them enters on {{date}}.",
            unnamed_one: "and {{count}} other",
            unnamed_other: "and {{count}} others",
            done: "OK"
        }
    },
    CycleRosterBulkActionDialog: {
        cancel: "Cancel",
        share: {
            title: "Share these reviews?",
            body: "Only submitted reviews will be shared.",
            visibilityHeading: "What the reviewees will be able to see:",
            visibility: {
                manager: "Manager answers they have access to",
                peerIdentified: "Peer answers they have access to, with reviewer names visible",
                peerAnonymous: "Peer answers they have access to, anonymized",
                self: "Their own self-review answers",
                upwardIdentified: "Upward review answers they have access to, with reviewer names visible",
                upwardAnonymous: "Upward review answers they have access to, anonymized"
            },
            laterEdits: "Any edits made after sharing will also be visible.",
            acknowledgmentNote: "This cycle requires acknowledgment, so you won't be able to unshare once shared.",
            confirm: "Share",
            pending: "Sharing…",
            doneToast: "The reviews were shared.",
            doneToastWithCount_one: "The review for {{count}} reviewee was shared.",
            doneToastWithCount_other: "The reviews for {{count}} reviewees were shared.",
            failedToast: "We couldn't share these reviews. Try again."
        },
        unshare: {
            title: "Unshare these reviews?",
            body: "Only shared reviews will be unshared. The reviewees will no longer be able to see them.",
            confirm: "Unshare",
            pending: "Unsharing…",
            doneToast: "The reviews were unshared.",
            doneToastWithCount_one: "The review for {{count}} reviewee was unshared.",
            doneToastWithCount_other: "The reviews for {{count}} reviewees were unshared.",
            failedToast: "We couldn't unshare these reviews. Try again."
        },
        remove: {
            title: "Remove these reviewees?",
            body: "This hides any reviews written about them in this cycle.",
            restorable: "If they are added back to the cycle, those reviews become visible again.",
            acknowledgmentNote: "Reviewees who have acknowledged their review won't be removed.",
            confirm: "Remove from cycle",
            pending: "Removing…",
            doneToast: "The reviewees were removed from the cycle.",
            doneToastWithCount_one: "{{count}} reviewee was removed from the cycle.",
            doneToastWithCount_other: "{{count}} reviewees were removed from the cycle.",
            failedToast: "We couldn't remove these reviewees. Try again."
        }
    },
    CycleRevieweeRowMenu: {
        ariaLabel: "Actions for {{name}}",
        temporaryManager: {
            label: "Assign a temporary manager",
            description: "Assign manager tasks to someone other than the manager, for this cycle.",
            unavailable: {
                locked: "This cycle is locked.",
                noManagerReview: "This cycle has no manager review.",
                unknownCycle: "Still loading this cycle."
            }
        },
        peer: {
            assignLabel: "Assign peer reviewers",
            assignDescription: "Choose who will be writing peer reviews about this reviewee.",
            updateLabel: "Update peer reviewers",
            updateDescription: "Reassign who will be writing peer reviews about this reviewee.",
            unavailable: {
                locked: "This cycle is locked.",
                acknowledged: "This review was acknowledged, so peer reviewers can't be changed.",
                unknownCycle: "Still loading this cycle."
            }
        },
        upward: {
            assignLabel: "Assign upward reviewers",
            assignDescription: "Choose who will be writing upward reviews about this reviewee.",
            updateLabel: "Update upward reviewers",
            updateDescription: "Reassign who will be writing upward reviews about this reviewee.",
            unavailable: {
                locked: "This cycle is locked.",
                acknowledged: "This review was acknowledged, so upward reviewers can't be changed.",
                unknownCycle: "Still loading this cycle."
            }
        },
        remove: {
            label: "Remove from review cycle",
            description: "Reviews about the person will no longer be needed.",
            unavailable: {
                locked: "This cycle is locked.",
                acknowledged: "This review was acknowledged, so the reviewee can't be removed.",
                unknownCycle: "Still loading this cycle."
            }
        }
    },
    CycleTemporaryManagerDialog: {
        title: "Reassign manager tasks for this review cycle",
        description: "The temporary manager can assign peers as well as write and share reviews for <strong>{{name}}</strong> for this specific review cycle. They will also gain access to their past reviews.",
        officialManagerHeading: "Official manager",
        currentHeading: "Temporary manager",
        noneAssigned: "Nobody — their reporting manager is on the hook.",
        clear: "Remove",
        loadingCurrent: "Checking who is assigned",
        loadCurrentFailed: "We couldn't load who is currently assigned, so saving is off — reopen the dialog to try again.",
        searchPlaceholder: "Search people",
        searchLoading: "Searching",
        searchNoResults: "No matching people",
        searchResults_one: "{{count}} person",
        searchResults_other: "{{count}} people",
        assignedToast: "{{manager}} is now acting as {{name}}'s manager for this cycle.",
        clearedToast: "{{name}}'s manager tasks went back to their reporting manager.",
        saveFailedToast: "We couldn't save the temporary manager. Try again.",
        cancel: "Cancel",
        save: "Assign",
        saving: "Saving…"
    },
    CycleReviewerPickerDialog: {
        loadingCurrent: "Checking who is assigned",
        loadCurrentFailed: "We couldn't load the current reviewers, so saving is off — reopen the dialog to try again.",
        chipRequests_one: "({{count}} request)",
        chipRequests_other: "({{count}} requests)",
        noneSelected: "Nobody is assigned yet.",
        removeReviewer: "Remove {{name}}",
        addDirectReports_one: "Add direct report ({{count}})",
        addDirectReports_other: "Add all direct reports ({{count}})",
        addReportingLine_one: "Add reporting line ({{count}})",
        addReportingLine_other: "Add whole reporting line ({{count}})",
        searchPlaceholder: "Search people",
        searchLoading: "Searching",
        searchNoResults: "No matching people",
        searchResults_one: "{{count}} person",
        searchResults_other: "{{count}} people",
        alreadyAssigned_one: "{{count}} review",
        alreadyAssigned_other: "{{count}} reviews",
        loadMoreCandidates: "Show more people",
        cancel: "Cancel",
        saving: "Saving…",
        peer: {
            title_one: "Ask {{count}} peer to complete a review",
            title_other: "Ask {{count}} peers to complete a review",
            description: "Identify the most relevant peers by confirming with <strong>{{name}}</strong> who they regularly collaborate or interact with.",
            fieldLabel: "Peer reviewers",
            notificationHint: "A notification will be sent to newly assigned and any unassigned peers.",
            totalSelected_one: "{{count}} total peer selected",
            totalSelected_other: "{{count}} total peers selected",
            save: "Update",
            savedToast_one: "{{count}} peer reviewer saved for {{name}}.",
            savedToast_other: "{{count}} peer reviewers saved for {{name}}.",
            saveFailedToast: "We couldn't save the peer reviewers. Try again."
        },
        upward: {
            title: "Assign upward reviewers",
            description: "Choose how upward reviewers are assigned for <strong>{{name}}</strong>. Reviewers will be notified that they need to complete this review.",
            fieldLabel: "Upward reviewers",
            totalSelected_one: "{{count}} total reviewer selected",
            totalSelected_other: "{{count}} total reviewers selected",
            save_one: "Assign {{count}} reviewer",
            save_other: "Assign {{count}} reviewers",
            savedToast_one: "{{count}} upward reviewer saved for {{name}}.",
            savedToast_other: "{{count}} upward reviewers saved for {{name}}.",
            saveFailedToast: "We couldn't save the upward reviewers. Try again."
        }
    },
    RemoveCycleRevieweeDialog: {
        title: "Remove {{name}} from this cycle?",
        body_zero: "This takes them out of the cycle. No reviews have been written about them yet.",
        body_one: "This hides the review written about them in this cycle.",
        body_other: "This hides the {{count}} reviews written about them in this cycle.",
        restorable: "If they are added back to the cycle, those reviews become visible again.",
        removedToast: "{{name}} was removed from the cycle.",
        removeFailedToast: "We couldn't remove this person. Try again.",
        cancel: "Cancel",
        confirm: "Remove from cycle",
        removing: "Removing…"
    },
    CycleRevieweeReviews: {
        noReviews: "No reviews have been created for this person yet.",
        unknownReviewer: "Unknown reviewer",
        dueOn: "Due {{date}}"
    },
    CycleRevieweeStatusBadge: {
        "not-started": "Not started",
        "in-progress": "In progress",
        submitted: "Submitted",
        overdue: "Overdue",
        "not-submitted": "Not submitted"
    },
    CycleDeliveryBadge: {
        "not-delivered": "Not delivered",
        shared: "Shared",
        acknowledged: "Acknowledged"
    },
    CycleRevieweeStateTag: {
        active: "Active",
        upcoming: "Upcoming",
        entersOn: "Enters {{date}}"
    },
    CycleRosterFilters: {
        search: {
            label: "Search reviewees",
            placeholder: "Search reviewees"
        },
        status: {
            self: "Self review",
            manager: "Manager review",
            peer: "Peer review",
            upward: "Upward review"
        },
        delivery: {
            label: "Delivery"
        },
        state: {
            label: "State"
        },
        exceptions: {
            label: "Exceptions",
            countsUnavailable: "We couldn't load the counts. You can still filter by these."
        },
        selectedCount: "{{count}} selected",
        remove: "Remove filter",
        clear: "Clear filters"
    },
    RosterAddFilterButton: {
        label: "Add filter",
        applied: "Applied",
        back: "Back to filters"
    },
    CycleRevieweeExceptions: {
        "no-manager": "No manager assigned",
        "no-peer-reviewers": "No peer reviewers",
        "no-upward-reviewers": "No upward reviewers",
        deactivated: "Deactivated"
    },
    ReviewCycleDetailView: {
        addReviewees: "Add reviewees",
        selectAllUnavailable: {
            searchTerm: "Clear the search to select every matching reviewee. You can still tick rows individually.",
            revieweeStates: "Clear the State filter to select every matching reviewee. You can still tick rows individually."
        },
        breadcrumbAriaLabel: "Breadcrumb",
        notLaunched: "This cycle hasn't launched yet, so there's no progress to report.",
        progressUnavailable: "We couldn't load the progress for this selection. The reviewees below are up to date.",
        nextIntake: "Next people enter {{date}}",
        totalCount_one: "{{count}} reviewee",
        totalCount_other: "{{count}} reviewees",
        noReviewees: "There are no reviewees you can see in this cycle.",
        noMatchingReviewees: "No reviewees match the filters you've applied.",
        error: "We couldn't load this review cycle",
        liveRegion: {
            loading: "Loading review cycle",
            loadingMoreAnnouncement: "Loading more reviewees",
            loadedMore_one: "{{count}} more reviewee loaded",
            loadedMore_other: "{{count}} more reviewees loaded",
            results_one: "{{count}} reviewee",
            results_other: "{{count}} reviewees",
            sorted: {
                name: "Reviewees sorted by name",
                "entry-date": "Reviewees sorted by entry date"
            }
        }
    },
    Errors: {
        reassurance: "Nothing is lost. Try again in a moment.",
        retry: "Try again",
        refreshBanner: "We couldn't refresh the list. Showing the previous results."
    },
    CycleNineBoxGrid: {
        percentage: "{{percentage}}%",
        notPlaced: "Not placed",
        placementRatings: "Performance {{performance}} · Potential {{potential}}",
        axis: {
            performance: "Performance",
            potential: "Potential",
            low: "Low",
            high: "High"
        },
        band: {
            Low: "Low",
            Medium: "Medium",
            High: "High"
        },
        cell: {
            enigma: "Enigma",
            growth: "Growth",
            stars: "Stars",
            dilemma: "Dilemma",
            core: "Core",
            "high-impact": "High impact",
            risk: "Risk",
            effective: "Effective",
            trusted: "Trusted",
            ariaLabel: "{{label}}, {{count}} placed, {{percentage}} percent. Performance {{performance}}, potential {{potential}}.",
            explainerAriaLabel: "What {{label}} means"
        },
        guidance: {
            risk: "Not meeting expectations with limited indicators of future growth. Address directly through a performance improvement plan or explore whether a different role is a better fit.",
            dilemma: "Has some capacity to grow but isn't meeting performance expectations today. Identify root causes and set specific, measurable goals. This is a decision point.",
            enigma: "High ceiling but not delivering today. May be new to the role, misaligned, or facing barriers. Diagnose what's blocking performance and set a clear timeline for improvement.",
            effective: "Meets baseline expectations without significant growth trajectory. Ensure role clarity and maintain engagement. Don't over-invest at the expense of higher-potential talent.",
            core: "Meets expectations and represents the stable foundation of the team. Invest in skill development and ensure they feel valued. Disengagement here is a quiet risk.",
            growth: "Shows strong growth potential but hasn't yet translated it into consistent high performance. Focus on closing the performance gap through coaching and clear expectations.",
            trusted: "A reliable expert who delivers consistently in their current role. Respect their depth, reward their impact, and avoid pushing toward roles that don't fit.",
            "high-impact": "Delivers strong results and may have room to grow with the right development. Recognize contributions and explore whether targeted investment can unlock more potential.",
            stars: "Consistently exceeds expectations and shows clear readiness for broader responsibility. Prioritize retention, stretch assignments, and succession planning."
        }
    },
    ReviewCycleCalibration: {
        totalCount_one: "{{count}} reviewee",
        totalCount_other: "{{count}} reviewees",
        error: "We couldn't load this cycle's calibration",
        columns: {
            reviewee: "Reviewee",
            placement: "9-box placement",
            reviewScore: "Review score"
        },
        noReviewees: "There are no reviewees you can see in this cycle.",
        noMatches: "No reviewees match your search.",
        stats: {
            groupAverage: "Avg review score",
            outOf: "/ {{scale}}",
            scoredOf: "{{scored}} of {{total}} reviewees scored",
            nobodyScored: "Nobody scored yet",
            withheld: "Too few people scored to show a figure",
            vsCycle: "vs Entire cycle",
            gap: "{{sign}}{{gap}}",
            above: "Above average",
            below: "Below average",
            inLine: "In line with the cycle"
        },
        grid: {
            expand: "Expand 9-box",
            hide: "Hide 9-box",
            noPlacement: "This cycle doesn't rate performance and potential, so there are no 9-box placements to show."
        },
        cellChip: {
            clear: "Clear this selection"
        },
        row: {
            expand: "Show calibration details for {{name}}",
            collapse: "Hide calibration details for {{name}}",
            notRated: "Not rated yet",
            unknownMember: "Unknown member"
        },
        search: {
            placeholder: "Find reviewee",
            ariaLabel: "Find reviewee"
        },
        liveRegion: {
            loading: "Loading cycle calibration",
            loadingMoreAnnouncement: "Loading more reviewees",
            loadedMore_one: "{{count}} more reviewee loaded",
            loadedMore_other: "{{count}} more reviewees loaded",
            results_one: "{{count}} reviewee",
            results_other: "{{count}} reviewees"
        }
    },
    CycleCalibrationPanel: {
        liveRegion: {
            loading: "Loading this reviewee's evidence"
        },
        scores: {
            heading: "Scores and ratings"
        },
        score: {
            review: "Review score",
            performance: "Performance",
            potential: "Potential",
            explainerAriaLabel: "How is {{score}} calculated?",
            outOf: "/ {{scale}}",
            notRated: "Not rated"
        },
        band: {
            Low: "Low",
            Medium: "Medium",
            High: "High"
        },
        benchmarks: {
            heading: "Benchmarks",
            description: "Across everyone in the cycle, not only the people you can see.",
            unavailable: "We couldn't load these comparisons. The scores and the note above are unaffected.",
            versus: "vs {{label}}",
            delta: "{{sign}}{{delta}}",
            none: "No department or role recorded for this person."
        },
        note: {
            label: "Calibration notes",
            audience: "Visible to whoever manages this person directly.",
            save: "Save note",
            saved: "Calibration note saved",
            saveFailed: "We couldn't save this calibration note. Your changes are still here — try again.",
            unavailable: "We couldn't load this calibration note, so it can't be edited right now.",
            openReview: "Open review"
        }
    },
    AddFilterButton: {
        label: "Add filter"
    },
    FilterKinds: {
        applied: "Applied",
        directManagers: "Direct managers",
        reportingLine: "Reporting lines",
        segment: "Segment",
        department: "Department",
        gender: "Gender",
        workLocation: "Work location",
        roleLevel: "Role level",
        jobTitle: "Job title",
        tenure: "Tenure",
        track: "Track"
    },
    FilterBar: {
        remove: "Remove filter",
        me: "Me",
        valueCount_one: "{{count}} selected",
        valueCount_other: "{{count}} selected",
        chipAriaLabel: "{{kind}}: {{value}}",
        clear: "Clear filters"
    },
    TrackOptions: {
        manager: "People manager",
        individualContributor: "Individual contributor"
    },
    DepartmentFilterPanel: {
        title: "Department",
        back: "Back to filters",
        searchPlaceholder: "Search department",
        empty: "No departments available",
        noMatches: "No departments found",
        loading: "Loading departments",
        liveRegion: {
            results_one: "{{count}} department found",
            results_other: "{{count}} departments found"
        },
        error: "Departments couldn't be loaded. Try again."
    },
    RoleLevelFilterPanel: {
        title: "Role level",
        back: "Back to filters",
        searchPlaceholder: "Search role level",
        empty: "No role levels available",
        noMatches: "No role levels found",
        loading: "Loading role levels",
        liveRegion: {
            results_one: "{{count}} role level found",
            results_other: "{{count}} role levels found"
        },
        error: "Role levels couldn't be loaded. Try again."
    },
    JobTitleFilterPanel: {
        title: "Job title",
        back: "Back to filters",
        searchPlaceholder: "Search job title",
        noResults: "No job titles match your search.",
        empty: "No job titles available",
        loading: "Loading job titles",
        staleError: "Couldn't refresh — showing the last results.",
        liveRegion: {
            results_one: "{{count}} job title found",
            results_other: "{{count}} job titles found",
            loadedMore_one: "Loaded {{count}} more job title",
            loadedMore_other: "Loaded {{count}} more job titles"
        },
        error: "Job titles couldn't be loaded. Try again."
    },
    TenureFilterPanel: {
        title: "Tenure",
        back: "Back to filters",
        clear: "Clear tenure"
    },
    TrackFilterPanel: {
        title: "Track",
        back: "Back to filters",
        clear: "Clear track"
    },
    GenderFilterPanel: {
        title: "Gender",
        back: "Back to filters",
        searchPlaceholder: "Search gender",
        empty: "No genders available",
        noMatches: "No genders found",
        loading: "Loading genders",
        liveRegion: {
            results_one: "{{count}} gender found",
            results_other: "{{count}} genders found"
        },
        error: "Genders couldn't be loaded. Try again."
    },
    WorkLocationFilterPanel: {
        title: "Work location",
        back: "Back to filters",
        searchPlaceholder: "Search work location",
        empty: "No work locations available",
        noMatches: "No work locations found",
        loading: "Loading work locations",
        liveRegion: {
            results_one: "{{count}} work location found",
            results_other: "{{count}} work locations found"
        },
        error: "Work locations couldn't be loaded. Try again."
    },
    DirectManagersFilterPanel: {
        title: "Direct managers",
        back: "Back to filters",
        myScope: "Me",
        searchPlaceholder: "Search manager",
        empty: "No managers available",
        noResults: "No managers match your search.",
        loading: "Loading managers",
        liveRegion: {
            results_one: "{{count}} manager found",
            results_other: "{{count}} managers found",
            loadedMore_one: "Loaded {{count}} more manager",
            loadedMore_other: "Loaded {{count}} more managers"
        },
        error: "Managers couldn't be loaded. Try again.",
        staleError: "Couldn't refresh — showing the last results."
    },
    ReportingLineFilterPanel: {
        title: "Reporting lines",
        back: "Back to filters",
        myScope: "My reporting line",
        searchPlaceholder: "Search manager",
        empty: "No managers available",
        noResults: "No managers match your search.",
        loading: "Loading managers",
        liveRegion: {
            results_one: "{{count}} manager found",
            results_other: "{{count}} managers found",
            loadedMore_one: "Loaded {{count}} more manager",
            loadedMore_other: "Loaded {{count}} more managers"
        },
        error: "Managers couldn't be loaded. Try again.",
        staleError: "Couldn't refresh — showing the last results."
    },
    SegmentFilterPanel: {
        title: "Segment",
        back: "Back to filters",
        searchPlaceholder: "Search segment",
        empty: "No segments available",
        noResults: "No segments match your search.",
        untitled: "Untitled segment",
        loading: "Loading segments",
        liveRegion: {
            results_one: "{{count}} segment found",
            results_other: "{{count}} segments found",
            loadedMore_one: "Loaded {{count}} more segment",
            loadedMore_other: "Loaded {{count}} more segments"
        },
        error: "Segments couldn't be loaded. Try again.",
        staleError: "Couldn't refresh — showing the last results."
    },
    ReviewCycleScores: {
        totalCount_one: "{{count}} reviewee",
        totalCount_other: "{{count}} reviewees",
        noScoresYet: "No rated reviews have come in yet. The distribution appears as scores arrive.",
        withheldDistribution: "Too few people have been scored to show a distribution without identifying them.",
        noReviewees: "There are no reviewees you can see in this cycle.",
        noMatches: "No reviewees match your search.",
        error: "We couldn't load this cycle's scores",
        search: {
            placeholder: "Find reviewee",
            ariaLabel: "Find reviewee"
        },
        bandChip: {
            below: "Score below {{max}}",
            between: "Score {{min}}–{{max}}",
            atLeast: "Score {{min}} and above",
            clear: "Clear this selection"
        },
        sort: {
            ascending: "Sorted by review score, lowest first",
            descending: "Sorted by review score, highest first"
        },
        liveRegion: {
            loading: "Loading cycle scores",
            loadingMoreAnnouncement: "Loading more reviewees",
            loadedMore_one: "{{count}} more reviewee loaded",
            loadedMore_other: "{{count}} more reviewees loaded",
            results_one: "{{count}} reviewee",
            results_other: "{{count}} reviewees"
        }
    },
    CycleScoreDistribution: {
        seriesLabel: "This cycle"
    },
    ReviewCycleQuestionResults: {
        error: "We couldn't load this cycle's question results",
        empty: {
            title: "No closed-ended questions",
            description: "This cycle asks only written questions. What those answers said is on the Themes tab."
        },
        kind: {
            ariaLabel: "Show questions of type",
            all: "All",
            rating: "Ratings",
            choice: "Multiple choice"
        },
        section: {
            label: "Section {{position}}: {{title}}"
        },
        liveRegion: {
            loading: "Loading question results"
        }
    },
    CycleQuestionBlock: {
        audience: {
            ariaLabel: 'Show responses to "{{question}}" from',
            all: "All"
        },
        meta: {
            responses_one: "{{count}} response",
            responses_other: "{{count}} responses",
            narrowed_one: "{{count}} response from {{reviewType}} reviewers",
            narrowed_other: "{{count}} responses from {{reviewType}} reviewers",
            singleAudience_one: "Asked of {{reviewType}} reviewers · {{count}} response",
            singleAudience_other: "Asked of {{reviewType}} reviewers · {{count}} responses"
        }
    },
    CycleQuestionAnswers: {
        noResponses: "No responses yet. Results appear as reviews are submitted.",
        withheld: {
            "below-minimum-cohort": "Too few people answered to show a breakdown without identifying them."
        },
        average: {
            value: "{{average, number(minimumFractionDigits: 1; maximumFractionDigits: 1)}}",
            outOf: "/ {{scaleMax}} average"
        },
        answer: {
            percentage: "{{percentage}}%",
            count: "({{count}})",
            valueLabel_one: "{{count}} response, {{percentage}}%",
            valueLabel_other: "{{count}} responses, {{percentage}}%"
        }
    },
    ScoreFormulaExplainer: {
        ariaLabel: "How is the review score calculated?",
        title: "How this score is calculated",
        share: "{{share}}%",
        answerOutOf: "{{value}} / {{scaleMax}}",
        noAnswer: "—",
        perspectiveReviewers_one: "{{perspective}} · {{count}} reviewer",
        perspectiveReviewers_other: "{{perspective}} · {{count}} reviewers",
        mode: {
            "average-all-questions": "The average of every rating question in this cycle, out of {{scale}}.",
            "average-manager-questions": "The average of the manager review's rating questions, out of {{scale}}.",
            custom: "A weighted combination of the questions below, out of {{scale}}.",
            "overall-score": "The cycle's overall score question, out of {{scale}}."
        }
    },
    CycleRevieweeScoresTable: {
        ariaLabel: "Reviewees by review score",
        unknownMember: "Unknown member",
        noScore: "No review score yet",
        columns: {
            person: "Person",
            reviewScore: "Review score",
            role: "Role",
            department: "Department"
        }
    },
    AverageScoreStat: {
        label: "Average review score",
        unit: "/ {{maxScale}}",
        ariaValue: "{{value}} out of {{maxScale}}",
        noScore: "No review score yet",
        basedOnCompletion: "Based on {{rate}}% completion"
    },
    TotalRevieweesStat: {
        label: "Total reviewees",
        unit_one: "Person",
        unit_other: "People"
    },
    ScoreBucketSelector: {
        label: "Score buckets",
        whole: "Broad",
        half: "Medium",
        quarter: "Detailed",
        unavailable: "Too many comparisons on screen to split the scale this finely"
    },
    ReviewScoreHistogram: {
        hint: "Select a bar to filter the list below",
        percentage: "{{percentage}}%",
        band: {
            ariaLabel_one: "{{label}}, {{count}} person, {{percentage}}%",
            ariaLabel_other: "{{label}}, {{count}} people, {{percentage}}%"
        },
        bar: {
            ariaLabel_one: "{{series}}, {{label}}, {{count}} person, {{percentage}}%",
            ariaLabel_other: "{{series}}, {{label}}, {{count}} people, {{percentage}}%"
        }
    },
    ScoreBandRanges: {
        below: "< {{max}}",
        between: "{{min}}–{{max}}",
        atLeast: "≥ {{min}}"
    },
    ReviewCycleThemes: {
        strengths: {
            title: "Strengths"
        },
        opportunities: {
            title: "Growth opportunities"
        },
        empty: "No written answer in this cycle has been themed yet.",
        emptyNoThemes: "The written answers in scope didn't surface a recurring theme.",
        emptyScoped: "Nobody in this scope submitted a written answer. Try removing a filter to widen the results.",
        derivation: {
            title: "How is this calculated?",
            body: "When a review is submitted, its written answers are read against a fixed set of twelve themes, and each mention is labelled from what was actually written rather than inferred. Every bar spans the whole cohort, so the remainder is the people a theme never came up for. Nothing here is attributed to a single reviewer or reviewee - treat it as an indicator, not a source of truth."
        },
        liveRegion: {
            loading: "Loading review themes"
        }
    },
    ThemesColumn: {
        strengthsEmpty: "No theme drew more praise than criticism.",
        watchOutsEmpty: "Every mentioned theme already appears under Strengths."
    },
    ThemeHeadlineCard: {
        labels: {
            strength: "Top strengths",
            watchArea: "Watch area",
            toMonitor: "To monitor"
        },
        body: {
            strength_one: "Across {{mentions}} theme mentions, {{first}} is the clearest strength.",
            strength_other: "Across {{mentions}} theme mentions, the clearest strengths are: {{first}}, {{second}}.",
            watchArea_one: "Criticism most outweighs praise in {{first}}.",
            watchArea_other: "Criticism most outweighs praise in: {{first}}, {{second}}.",
            toMonitor_one: "{{first}} sits near even. It is worth watching for shifts next cycle.",
            toMonitor_other: "Closest to an even split: {{first}}, {{second}}. Both are worth watching for shifts next cycle."
        },
        empty: {
            strength: "No widely mentioned theme stands out as a clear strength.",
            watchArea: "No widely mentioned theme has criticism clearly outweighing praise.",
            toMonitor: "No widely mentioned theme sits near an even split."
        }
    },
    ThemeSentimentBar: {
        segment: "{{percent}}% {{label}}",
        segmentSeparator: ", ",
        labels: {
            Positive: "positive",
            Mixed: "mixed",
            Negative: "negative",
            remaining: "not mentioned"
        }
    }
};
